import { Building2, Search, Users, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useDirectory } from "@/features/members/hooks/useDirectory";
import { buildRegions } from "@/lib/locations";
import { DirectoryGridSkeleton } from "@/components/Skeletons";
import { LegacyCard } from "@/components/LegacyCard";
import { MemberCard } from "@/components/MemberCard";
import { Button } from "@/components/ui/button";
import { PreviewBanner, PreviewCutoff } from "@/components/PreviewCutoff";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ALL = "__all__";



const tabFromParams = (value: string | null) =>
  value === "organizations" ? "organizations" : "people";

export default function DirectoryPage() {
  // ?search= and ?tab= let the ⌘K palette land on a specific result, and are
  // written back as you browse — so a reload keeps the tab you were on and the
  // URL is what you send someone. `replace`, not push: a tab is where you are,
  // not somewhere you went, and Back should leave the directory rather than
  // walk through every switch.
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const search = params.get("search") ?? "";
  const tab = tabFromParams(params.get("tab"));
  const [region, setRegion] = useState(ALL);
  // Any of the chosen mediums matches: a steward of a painter and a sculptor
  // should surface for either.
  const [chosenMediums, setChosenMediums] = useState<string[]>([]);

  const setParam = (key: string, value: string) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );
  const setSearch = (value: string) => setParam("search", value);
  const setTab = (value: string) => setParam("tab", value === "people" ? "" : value);
  const { data, error } = useDirectory(search);
  const allMembers = data?.members;
  const allOrganizations = data?.organizations;

  const { regions, regionsOf, keywords } = useMemo(() => {
    const keywords = new Set<string>();
    for (const m of allMembers ?? []) (m.mediums ?? []).forEach((k) => keywords.add(k));
    for (const o of allOrganizations ?? []) o.mediums.forEach((k) => keywords.add(k));
    // Regions are places, not spellings: members type their location however
    // they like, and the filter used to list Brooklyn ten times. See
    // lib/locations. The card still shows what the member wrote.
    const index = buildRegions([
      ...(allMembers ?? []).map((m) => m.location),
      ...(allOrganizations ?? []).map((o) => o.location),
    ]);
    // `Array.prototype.sort()` with no comparator orders by UTF-16 code unit,
    // which is not alphabetical: every capital letter sorts before every
    // lowercase one, and an accented character lands after the whole ASCII
    // range. Mediums carry the form's own capitalisation.
    const alphabetically = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" });
    return {
      regions: index.options,
      regionsOf: index.keysOf,
      keywords: Array.from(keywords).sort(alphabetically),
    };
  }, [allMembers, allOrganizations]);

  const members = allMembers?.filter(
    (m) =>
      (region === ALL || regionsOf(m.location).includes(region)) &&
      (chosenMediums.length === 0 || (m.mediums ?? []).some((x) => chosenMediums.includes(x)))
  );
  const organizations = allOrganizations?.filter(
    (o) =>
      (region === ALL || regionsOf(o.location).includes(region)) &&
      (chosenMediums.length === 0 || o.mediums.some((x) => chosenMediums.includes(x)))
  );

  const loading = members === undefined || organizations === undefined;
  const preview = Boolean(data?.preview);

  /* The filters stay on the page for somebody who is not a member, opening to
     the same regions and mediums, because that is part of saying what the
     directory holds. Choosing one asks them to join instead of narrowing, the
     same bargain the library makes: the list is a capped sample, and an axis
     you can narrow is a way of walking past the cap a facet at a time.

     It used to narrow the sample silently, which read as a broken control. */
  const askToJoin = () => {
    if (!preview) return false;
    toast("The directory is for members", {
      description:
        "You can see who is in it. Filtering and full profiles come with membership.",
      action: { label: "Apply", onClick: () => navigate("/apply") },
    });
    return true;
  };
  // Fall back to what was returned when the backend predates `totals`.
  const totalMembers = data?.totals?.members ?? members?.length ?? 0;
  const totalOrganizations =
    data?.totals?.organizations ?? organizations?.length ?? 0;
  const hiddenMembers = Math.max(0, totalMembers - (members?.length ?? 0));

  return (
    <div className="max-w-5xl">
      <div>
        <h1 className="text-2xl">Directory</h1>
        {loading ? (
          <Skeleton className="mt-1.5 h-4 w-48" />
        ) : (
          /* Real size, not the number of cards below: a preview shows a
             sample, and "20 members" undersells the directory to the person
             deciding whether to join. */
          <p className="text-muted-foreground mt-1 text-sm">
            {totalMembers} active {totalMembers === 1 ? "member" : "members"} ·{" "}
            {totalOrganizations}{" "}
            {totalOrganizations === 1 ? "organization" : "organizations"}
          </p>
        )}
      </div>

      <PreviewBanner show={preview && !loading}>
        Names, locations and contact details are available to members only.
      </PreviewBanner>

      {error && <p className="text-destructive mt-6 text-sm">{error.message}</p>}

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="people">
            <Users data-slot="icon" />
            Artists &amp; Stewards {loading ? "" : `(${members.length})`}
          </TabsTrigger>
          <TabsTrigger value="organizations">
            <Building2 data-slot="icon" />
            Organizations {loading ? "" : `(${organizations.length})`}
          </TabsTrigger>
        </TabsList>

        {/* Search and filters sit directly over the results they narrow, and
            both tabs share them — the term is one search across people and
            organizations, so it would be wrong to put a copy inside each. */}
        <div className="mt-2 mb-2 flex flex-wrap items-center gap-2">
          {/* Searching the directory is a full-member benefit, so the box is
              simply absent in the preview — the cards are visibly redacted and
              the cutoff at the foot of the page says what is behind it, which
              is two explanations more than a reader needs. */}
          {preview ? null : (
            <div className="relative w-full sm:max-w-sm">
              <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                className="pl-9"
                placeholder="Search people, organizations, mediums…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <Select
            value={region}
            onValueChange={(value) => {
              if (askToJoin()) return;
              setRegion(value);
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All regions</SelectItem>
              {regions.map((r) => (
                <SelectItem key={r.key} value={r.key}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-44 justify-between font-normal">
                {chosenMediums.length === 0
                  ? "Medium"
                  : chosenMediums.length === 1
                    ? chosenMediums[0]
                    : `${chosenMediums.length} mediums`}
                <ChevronDown className="text-muted-foreground size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {keywords.map((k) => (
                <DropdownMenuCheckboxItem
                  key={k}
                  checked={chosenMediums.includes(k)}
                  onCheckedChange={(on) => {
                    if (askToJoin()) return;
                    setChosenMediums(
                      on ? [...chosenMediums, k] : chosenMediums.filter((x) => x !== k)
                    );
                  }}
                  onSelect={(e) => e.preventDefault()}
                >
                  {k}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {(region !== ALL || chosenMediums.length > 0 || search) && (
            <Button
              variant="ghost"
              onClick={() => {
                setRegion(ALL);
                setChosenMediums([]);
                setSearch("");
              }}
            >
              Clear
            </Button>
          )}
        </div>

        <TabsContent value="people">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              <DirectoryGridSkeleton />
            ) : (
              members.map((m, i) => (
                <MemberCard key={m.id} member={m} index={i} />
              ))
            )}
          </div>
          {!loading && members.length === 0 && (
            <p className="animate-page-enter text-muted-foreground mt-12 text-center">
              No members match the current search and filters.
            </p>
          )}
          <PreviewCutoff hidden={hiddenMembers} show={preview && !loading} />
        </TabsContent>

        <TabsContent value="organizations">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              <DirectoryGridSkeleton />
            ) : (
              organizations.map((o, i) => (
                <LegacyCard key={o.id} legacy={o} index={i} />
              ))
            )}
          </div>
          {!loading && organizations.length === 0 && (
            <p className="animate-page-enter text-muted-foreground mt-12 text-center">
              No organizations match the current search and filters.
            </p>
          )}
          <PreviewCutoff
            hidden={Math.max(0, totalOrganizations - (organizations?.length ?? 0))}
            show={preview && !loading}
            noun="organizations"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
